/**
 * Запас хода и ликвидность.
 *
 * Две разные величины, которые нельзя описывать одними словами:
 *
 * - **запас хода** — на сколько дней хватит при обычном темпе, с учётом
 *   платежей на всём показываемом интервале. Платёж на 31-й день не должен
 *   теряться в обещании «хватит на 90 дней»;
 * - **ликвидность** — сколько дней жизни лежит на счетах **сейчас**, без
 *   будущих платежей и без резервов, которые уже закреплены под требования.
 *
 * Обе честно отвечают «недостаточно данных», когда расходы неизвестны:
 * при нулевых baseline прежний `pace` выдавал бесконечный запас именно тем,
 * кто ещё ничего о себе не рассказал.
 */
import { ZERO, add, clampPos, sub, type Money } from './money.js';
import { addDays, diffDays, type PlainDate } from './date.js';
import { isKnown, type Amount, type Answer } from './amount.js';
import { forecast, livingPerDay, notCovered } from './forecast.js';
import { paymentInstances } from './instances.js';
import type { Account, CreditTerms, Obligation, Reserve } from './types.js';

/** Сколько дней вперёд показываем по умолчанию. */
export const RUNWAY_HORIZON_DAYS = 90;

export interface RunwayInput {
  today: PlainDate;
  accounts: readonly Account[];
  reserves?: readonly Reserve[];
  obligations: readonly Obligation[];
  needBaseline: Amount;
  wantBaseline: Amount;
  /** Интервал показа. Платежи учитываются на всём его протяжении. */
  horizonDays?: number;
  /** Условия карт: платёж по выписке съедает запас хода так же, как ипотека. */
  creditTerms?: readonly CreditTerms[];
  /** С какого дня приложение ведёт учёт: граница просрочки платежей. */
  trackingStartedAt?: PlainDate;
}

export type RunwayResult = Answer<{
  /** На сколько дней хватит. Равен горизонту, если за него не вышли. */
  days: number;
  /** Последний день, на который денег хватает. `null` — за горизонтом показа. */
  until: PlainDate | null;
  /** Обычный дневной расход — знаменатель обеих величин. */
  usualPerDay: Money;
  /** Свободные деньги на счетах: ликвидное минус закреплённое. */
  liquid: Money;
  /** Платежи, попавшие в интервал, — обязательные и по картам. */
  payments: Money;
  /** Деньги не кончаются в пределах горизонта. */
  beyondHorizon: boolean;
}>;

/**
 * Свободные деньги: ликвидные счета минус конверты **на них же**.
 *
 * Конверт на неликвидном счёте второй раз не вычитается — этих денег и так
 * нет в ликвидных, и повторный вычет занижал бы картину.
 */
export function liquidFree(
  accounts: readonly Account[],
  reserves: readonly Reserve[] = [],
): { liquid: Money; reserved: Money; free: Money } {
  const liquidAccounts = accounts.filter((a) => a.isLiquid && !a.archived && a.kind !== 'credit_card');
  const ids = new Set(liquidAccounts.map((a) => a.id));
  const liquid = add(...liquidAccounts.map((a) => a.balance));
  const reserved = add(...reserves.filter((r) => ids.has(r.accountId)).map((r) => r.amount));
  return { liquid, reserved, free: clampPos(sub(liquid, reserved)) };
}

const perDay = (need: Amount, want: Amount): Money =>
  livingPerDay(isKnown(need) ? need.value : ZERO, isKnown(want) ? want.value : ZERO);

/** Чего не хватает для ответа про дни. */
function missingSpending(need: Amount, want: Amount): string[] {
  const missing: string[] = [];
  if (!isKnown(need)) missing.push('needBaseline');
  if (!isKnown(want)) missing.push('wantBaseline');
  return missing;
}

/**
 * Сколько дней продержатся деньги при обычном темпе.
 *
 * Считается по дням, а не делением: только так платёж на 31-й день попадает
 * в ответ про 90 дней.
 */
export function computeRunway(input: RunwayInput): RunwayResult {
  const missing = missingSpending(input.needBaseline, input.wantBaseline);
  if (missing.length > 0) {
    return {
      known: false,
      missing,
      action: 'Уточни обычные траты — без них нельзя сказать, на сколько хватит денег',
    };
  }

  const horizonDays = input.horizonDays ?? RUNWAY_HORIZON_DAYS;
  const end = addDays(input.today, horizonDays);
  const usualPerDay = perDay(input.needBaseline, input.wantBaseline);
  // Ноль в день — не темп, а пустые поля: так сохранялся пропущенный шаг
  // онбординга, и запас хода выходил «90 дней» при нуле на счетах.
  if (usualPerDay <= ZERO) {
    return {
      known: false,
      missing: ['needBaseline'],
      action: 'Уточни обычные траты — без них нельзя сказать, на сколько хватит денег',
    };
  }
  const { free } = liquidFree(input.accounts, input.reserves ?? []);

  /*
    События — из общего прогноза: платежи, кредитки, жизнь. Поступлений нет
    намеренно: запас хода отвечает, сколько продержатся деньги **без** них.

    Считается от свободных денег, а платёж просит только то, что под него
    ещё не отложено. Прежде вычиталось всё неоплаченное — и отложенное под
    налоги владельца уходило дважды: из свободных денег и в день платежа.
    А платёж по кредитке не вычитался вовсе.
  */
  const f = forecast({
    today: input.today,
    to: end,
    accounts: input.accounts,
    creditTerms: input.creditTerms ?? [],
    incomeStreams: [],
    obligations: input.obligations,
    ...(input.reserves ? { reserves: input.reserves } : {}),
    needBaseline: isKnown(input.needBaseline) ? input.needBaseline.value : ZERO,
    wantBaseline: isKnown(input.wantBaseline) ? input.wantBaseline.value : ZERO,
    ...(input.trackingStartedAt ? { trackingStartedAt: input.trackingStartedAt } : {}),
  });
  const dues = f.events.filter((e) => e.kind === 'payment' || e.kind === 'card');
  const payments = add(...dues.map((e) => e.amount));
  const dueOn = new Map<PlainDate, Money>();
  for (const e of dues) dueOn.set(e.date, add(dueOn.get(e.date) ?? ZERO, notCovered(e)));

  let balance = free;
  for (let i = 0; i <= horizonDays; i++) {
    const day = addDays(input.today, i);
    balance = sub(balance, dueOn.get(day) ?? ZERO);
    // Сегодняшний день жизни частично уже прожит — второй раз не списываем.
    if (i > 0) balance = sub(balance, usualPerDay);
    if (balance < ZERO) {
      // День `i` уже не обеспечен, значит хватило предыдущего. «Хватит до
      // 29 октября» и «29 октября деньги кончатся» — разные обещания, и
      // продукт даёт первое: обещать лишний день нельзя.
      const lasted = Math.max(0, i - 1);
      return {
        known: true, days: lasted, until: addDays(input.today, lasted),
        usualPerDay, liquid: free, payments, beyondHorizon: false,
      };
    }
  }

  return {
    known: true, days: horizonDays, until: null, usualPerDay, liquid: free, payments,
    beyondHorizon: true,
  };
}

export type LiquiditySafety = 'critical' | 'low' | 'adequate' | 'strong';

/** Пороги в днях жизни. Гипотеза, требующая калибровки. */
export const LIQUIDITY_DAYS = { low: 7, adequate: 30, strong: 90 } as const;

export type LiquidityResult = Answer<{
  safety: LiquiditySafety;
  /** Дней жизни на счетах. `null` — расходов нет вовсе. */
  days: number | null;
  free: Money;
  reserved: Money;
  /** Ближайший платёж не обеспечен свободными деньгами. */
  nearestPaymentUncovered: boolean;
}>;

/**
 * Сколько человек проживёт на том, что лежит на счетах **сейчас**.
 *
 * От запаса хода отличается тем, что не смотрит в будущее: это состояние, а
 * не прогноз. Подушка на вкладе может быть собрана, а на карте пусто до
 * пятницы — и это две разные правды, обе нужные.
 */
export function computeLiquidity(input: {
  today: PlainDate;
  accounts: readonly Account[];
  reserves?: readonly Reserve[];
  obligations: readonly Obligation[];
  needBaseline: Amount;
  wantBaseline: Amount;
}): LiquidityResult {
  const missing = missingSpending(input.needBaseline, input.wantBaseline);
  if (missing.length > 0) {
    return {
      known: false,
      missing,
      action: 'Уточни обычные траты — без них запас в днях посчитать нельзя',
    };
  }

  const { free, reserved } = liquidFree(input.accounts, input.reserves ?? []);
  const usualPerDay = perDay(input.needBaseline, input.wantBaseline);

  // Ближайший неоплаченный платёж месяца: если свободных денег на него не
  // хватает, положение критическое независимо от числа дней.
  const upcoming = paymentInstances({
    obligations: input.obligations,
    today: input.today,
    from: input.today,
    to: addDays(input.today, 30),
  }).filter((i) => !i.settled);
  const nearest = upcoming[0];
  const nearestPaymentUncovered = nearest ? free < nearest.amount : false;

  if (usualPerDay <= ZERO) {
    // Подтверждённый ноль расходов: деньги не кончаются сами по себе.
    return {
      known: true,
      safety: nearestPaymentUncovered ? 'critical' : 'strong',
      days: null, free, reserved, nearestPaymentUncovered,
    };
  }

  const days = Math.floor(free / usualPerDay);
  const safety: LiquiditySafety = nearestPaymentUncovered || days < LIQUIDITY_DAYS.low
    ? 'critical'
    : days < LIQUIDITY_DAYS.adequate ? 'low'
      : days < LIQUIDITY_DAYS.strong ? 'adequate'
        : 'strong';

  return { known: true, safety, days, free, reserved, nearestPaymentUncovered };
}

/** Дней между датами — для подписи «хватит примерно до…». */
export const daysUntil = (from: PlainDate, to: PlainDate): number => diffDays(from, to);
